import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ExternalLink, ShoppingBag, Pencil, Trash2, Plus, RefreshCcw, ImagePlus, X as XIcon, Sprout } from "lucide-react";
import { LinkPreviewThumb } from "./LinkPreviewThumb";

/**
 * Shop This — repeatable list of items the poster used.
 * Every item automatically carries an "Affiliate link" disclosure.
 * Not available on Trusted branches (server enforces).
 *
 * Renders as a labeled brass panel; the parent decides layout (sticky
 * sidebar on desktop, inline on mobile). Pass `variant="sidebar"` to
 * enable the sticky wrapper.
 */
export function ShopThis({ idea, isAuthor, onIdeaUpdated, variant = "inline" }) {
  const navigate = useNavigate();
  const items = idea?.shop_items || [];
  const [drafting, setDrafting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ item_name: "", url: "", store_name: "", price_hint: "", related_idea_id: "" });
  // Author's own branches — used to populate the "link to my review" dropdown
  // on Shop This items. Fetched once per session on the author's first
  // interaction with the form. Excludes the current branch from the list
  // (you can't link a branch's shop item back to itself).
  const [myBranches, setMyBranches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const photoInputRef = useRef(null);
  const photoTargetRef = useRef(null);

  if (!idea || idea.category === "Trusted") return null;
  // Show nothing for viewers if there are no items and they aren't the author
  if (!isAuthor && items.length === 0) return null;

  const refreshPreview = async (itemId) => {
    setRefreshingId(itemId);
    try {
      const { data: updated } = await api.post(`/ideas/${idea.id}/shop-items/${itemId}/refresh-preview`);
      onIdeaUpdated?.({
        ...idea,
        shop_items: (idea.shop_items || []).map((i) => (i.id === itemId ? updated : i)),
      });
      toast(updated.preview_image_url ? "Preview refreshed." : "Retailer still isn't sharing a photo — you can upload your own.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast(typeof d === "string" ? d : "Could not refresh preview.");
    } finally {
      setRefreshingId(null);
    }
  };

  const openPhotoPicker = (itemId) => {
    photoTargetRef.current = itemId;
    photoInputRef.current?.click();
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    const itemId = photoTargetRef.current;
    if (!file || !itemId) return;
    setUploadingId(itemId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: updated } = await api.patch(
        `/ideas/${idea.id}/shop-items/${itemId}/photo`,
        { path: up.path },
      );
      onIdeaUpdated?.({
        ...idea,
        shop_items: (idea.shop_items || []).map((i) => (i.id === itemId ? updated : i)),
      });
      toast("Your own photo is now shown.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast(typeof d === "string" ? d : "Could not upload the photo.");
    } finally {
      setUploadingId(null);
      photoTargetRef.current = null;
      if (e.target) e.target.value = "";
    }
  };

  const clearOverride = async (itemId) => {
    setUploadingId(itemId);
    try {
      const { data: updated } = await api.patch(
        `/ideas/${idea.id}/shop-items/${itemId}/photo`,
        { path: null },
      );
      onIdeaUpdated?.({
        ...idea,
        shop_items: (idea.shop_items || []).map((i) => (i.id === itemId ? updated : i)),
      });
      toast("Your photo removed. Falling back to retailer preview.");
    } catch (err) {
      toast("Could not remove the photo.");
    } finally {
      setUploadingId(null);
    }
  };

  const promoteToBranch = (item) => {
    // Stash the source item in sessionStorage so PlantIdea can pick it up on
    // mount. We use sessionStorage instead of query params so photo paths /
    // URLs stay off the address bar and so a refresh keeps the prefill until
    // the user submits or discards it.
    try {
      sessionStorage.setItem(
        "willoah.plant.shop_prefill.v1",
        JSON.stringify({
          from_idea_id: idea.id,
          from_item_id: item.id,
          title: item.item_name || "",
          photo_path: item.preview_image_override_path || null,
          shop_item: {
            item_name: item.item_name || "",
            url: item.url || "",
            store_name: item.store_name || "",
            price_hint: item.price_hint || "",
            // Carry the already-fetched preview over so the new branch's
            // Shop This section shows an image immediately, instead of
            // re-scraping (which retailers like Amazon frequently block).
            preview_image_url: item.preview_image_url || null,
            preview_image_override_path: item.preview_image_override_path || null,
          },
          stashed_at: new Date().toISOString(),
        }),
      );
    } catch {}
    toast("Prefilled — just add the missing pieces.");
    navigate("/plant?from=shop-item");
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      item_name: item.item_name || "",
      url: item.url || "",
      store_name: item.store_name || "",
      price_hint: item.price_hint || "",
      related_idea_id: item.related_idea_id || "",
    });
    setDrafting(false);
    void ensureBranchesLoaded();
  };

  const cancel = () => {
    setDrafting(false);
    setEditingId(null);
    setForm({ item_name: "", url: "", store_name: "", price_hint: "", related_idea_id: "" });
  };

  // Lazy: only fetch the author's other branches when they open the form.
  const ensureBranchesLoaded = async () => {
    if (myBranches !== null || !isAuthor || !idea?.author_id) return;
    try {
      const { data } = await api.get(`/users/${idea.author_id}`);
      const list = (data?.ideas || []).filter((i) => i.id !== idea.id);
      setMyBranches(list);
    } catch {
      setMyBranches([]);
    }
  };

  const submit = async () => {
    if (!form.item_name.trim()) return toast("Item name is required.");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/ideas/${idea.id}/shop-items/${editingId}`, form);
        toast("Item updated.");
      } else {
        await api.post(`/ideas/${idea.id}/shop-items`, form);
        toast("Item added to Shop This.");
      }
      const { data } = await api.get(`/ideas/${idea.id}`);
      onIdeaUpdated?.(data);
      cancel();
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not save item.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.item_name}" from Shop This?`)) return;
    try {
      await api.delete(`/ideas/${idea.id}/shop-items/${item.id}`);
      const { data } = await api.get(`/ideas/${idea.id}`);
      onIdeaUpdated?.(data);
      toast("Item removed.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not remove item.");
    }
  };

  const wrapClass =
    variant === "sidebar"
      ? "lg:sticky lg:top-24"
      : "";

  return (
    <section
      data-testid={`shop-this-${variant}`}
      className={`${wrapClass} rounded-2xl border border-[#B79256]/40 bg-[#B79256]/8 p-5`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-4 h-4 text-[#B79256]" strokeWidth={2} />
          <h3 className="font-serif-display text-2xl text-[#2A2823]">Shop This</h3>
        </div>
        {items.length > 0 && (
          <span className="accent-label text-[10px] rounded-full bg-[#B79256]/25 text-[#8B6E3F] px-2 py-0.5">
            Affiliate links
          </span>
        )}
      </div>
      <p className="text-xs text-[#2A2823]/60 mt-1 italic">
        Links here may be the sharer's own affiliate links.
      </p>

      {items.length === 0 && !drafting && (
        <p data-testid="shop-this-empty" className="text-sm text-[#2A2823]/55 mt-4">
          {isAuthor ? "Add the pieces you actually used." : "No items shared yet."}
        </p>
      )}

      <ul className="mt-4 space-y-3">
        {items.map((item) => (
          <li key={item.id} data-testid={`shop-item-${item.id}`}>
            {editingId === item.id ? (
              <ShopItemForm form={form} setForm={setForm} onCancel={cancel} onSubmit={submit} busy={busy} myBranches={myBranches} />
            ) : (
              <div className="rounded-xl bg-white/80 border border-[#B79256]/25 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <LinkPreviewThumb
                      src={item.preview_image_url}
                      overridePath={item.preview_image_override_path}
                      alt={item.item_name}
                      size="md"
                      testId={`shop-item-preview-${item.id}`}
                    />
                    <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-base text-[#2A2823] leading-tight">
                      {item.item_name}
                    </div>
                    {(item.store_name || item.price_hint) && (
                      <div className="accent-label text-[10px] text-[#2A2823]/60 mt-1">
                        {item.store_name || ""}
                        {item.store_name && item.price_hint ? " · " : ""}
                        {item.price_hint || ""}
                      </div>
                    )}
                    {(item.url || item.related_idea_id) && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener nofollow sponsored"
                            data-testid={`shop-item-link-${item.id}`}
                            className="inline-flex items-center gap-1 text-[#4E5A3D] hover:text-[#3E4830] underline underline-offset-2"
                          >
                            View product <ExternalLink className="w-3 h-3" strokeWidth={2} />
                          </a>
                        )}
                        {item.related_idea_id && (
                          <Link
                            to={`/idea/${item.related_idea_id}`}
                            data-testid={`shop-item-review-${item.id}`}
                            className="inline-flex items-center gap-1 text-[#8B6E3F] hover:text-[#6b5430] underline underline-offset-2"
                            title="A seed on Willoah where the author reviews this exact item"
                          >
                            <Sprout className="w-3 h-3" strokeWidth={2} />
                            Read my review
                          </Link>
                        )}
                      </div>
                    )}
                    {/* Author-only photo controls. Only visible on the author's
                        own branches; solve the "retailer blocks scraping" case
                        by letting the author show their own real photo. */}
                    {isAuthor && (
                      <button
                        type="button"
                        data-testid={`shop-item-promote-${item.id}`}
                        onClick={() => promoteToBranch(item)}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#4E5A3D] hover:text-[#3E4830]"
                        title="Turn this into a standalone branch with its own context + proof-of-use"
                      >
                        <Sprout className="w-3 h-3" strokeWidth={2} />
                        turn into a full idea
                      </button>
                    )}
                    {isAuthor && item.url && (
                      <>
                        {/* When the retailer refused to share a photo, surface
                            a prominent dashed drop-zone + colored CTA so the
                            author immediately sees they can upload their own.
                            This is the escape hatch for Amazon-style scraping
                            blocks and is deliberately hard to miss. */}
                        {!item.preview_image_url && !item.preview_image_override_path && (
                          <button
                            type="button"
                            data-testid={`shop-item-upload-photo-empty-${item.id}`}
                            onClick={() => openPhotoPicker(item.id)}
                            disabled={uploadingId === item.id}
                            className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#B79256]/70 bg-[#B79256]/15 hover:bg-[#B79256]/25 text-[#8B6E3F] hover:text-[#6b5430] py-3 px-3 transition-colors disabled:opacity-60"
                            title="Amazon and some other stores block preview scraping — add your own photo so shoppers can see it"
                          >
                            <ImagePlus className="w-4 h-4" strokeWidth={2} />
                            <span className="text-xs font-medium">
                              {uploadingId === item.id ? "Uploading your photo…" : "Retailer didn't share a photo — add your own"}
                            </span>
                          </button>
                        )}
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                          {item.preview_image_override_path ? (
                            <button
                              type="button"
                              data-testid={`shop-item-clear-photo-${item.id}`}
                              onClick={() => clearOverride(item.id)}
                              disabled={uploadingId === item.id}
                              className="inline-flex items-center gap-1 text-[#B24A2A] hover:text-[#963e22]"
                            >
                              <XIcon className="w-3 h-3" strokeWidth={2} />
                              remove my photo
                            </button>
                          ) : (
                            <>
                              {/* Keep a small text-only link when a retailer
                                  preview is already showing, letting the
                                  author swap in their own if they prefer. */}
                              {item.preview_image_url && (
                                <button
                                  type="button"
                                  data-testid={`shop-item-upload-photo-${item.id}`}
                                  onClick={() => openPhotoPicker(item.id)}
                                  disabled={uploadingId === item.id}
                                  className="inline-flex items-center gap-1 text-[#4E5A3D] hover:text-[#3E4830]"
                                  title="Use your own photo instead of the retailer's preview"
                                >
                                  <ImagePlus className="w-3 h-3" strokeWidth={2} />
                                  {uploadingId === item.id ? "uploading…" : "use my own photo"}
                                </button>
                              )}
                              <button
                                type="button"
                                data-testid={`shop-item-refresh-${item.id}`}
                                onClick={() => refreshPreview(item.id)}
                                disabled={refreshingId === item.id}
                                className="inline-flex items-center gap-1 text-[#2A2823]/60 hover:text-[#4E5A3D]"
                                title="Retry fetching the retailer's photo"
                              >
                                <RefreshCcw className={`w-3 h-3 ${refreshingId === item.id ? "animate-spin" : ""}`} strokeWidth={2} />
                                {refreshingId === item.id ? "retrying…" : "retry preview"}
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    </div>
                  </div>
                  {isAuthor && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        type="button"
                        data-testid={`shop-item-edit-${item.id}`}
                        onClick={() => startEdit(item)}
                        className="p-1 rounded hover:bg-[#B79256]/15 text-[#2A2823]/50 hover:text-[#4E5A3D]"
                        aria-label="Edit item"
                      >
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        data-testid={`shop-item-delete-${item.id}`}
                        onClick={() => remove(item)}
                        className="p-1 rounded hover:bg-[#B24A2A]/15 text-[#2A2823]/50 hover:text-[#B24A2A]"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {isAuthor && (
        <div className="mt-4">
          {drafting ? (
            <ShopItemForm form={form} setForm={setForm} onCancel={cancel} onSubmit={submit} busy={busy} myBranches={myBranches} />
          ) : (
            !editingId && (
              <button
                type="button"
                data-testid="shop-this-add"
                onClick={() => { setDrafting(true); setForm({ item_name: "", url: "", store_name: "", price_hint: "", related_idea_id: "" }); void ensureBranchesLoaded(); }}
                className="inline-flex items-center gap-1.5 accent-label text-[#4E5A3D] hover:text-[#3E4830] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                Add an item
              </button>
            )
          )}
        </div>
      )}
      {/* Hidden file input for the "add my own photo" flow. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        onChange={onPhotoPicked}
        className="hidden"
        data-testid="shop-item-photo-input"
      />
    </section>
  );
}

function ShopItemForm({ form, setForm, onCancel, onSubmit, busy, myBranches }) {
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="space-y-2 rounded-xl bg-white/80 border border-[#B79256]/30 p-3">
      <Input
        data-testid="shop-form-item-name"
        value={form.item_name}
        onChange={change("item_name")}
        placeholder="What's the item? (e.g. mixing bowl)"
        className="text-sm rounded-xl bg-[#FBF8F1] border-[#B79256]/40"
        maxLength={120}
        required
      />
      <Input
        data-testid="shop-form-url"
        value={form.url}
        onChange={change("url")}
        placeholder="Optional link (must start with http:// or https://)"
        className="text-sm rounded-xl bg-[#FBF8F1] border-[#B79256]/40"
      />
      <div className="grid grid-cols-2 gap-2">
        <Input
          data-testid="shop-form-store"
          value={form.store_name}
          onChange={change("store_name")}
          placeholder="Store (optional)"
          className="text-sm rounded-xl bg-[#FBF8F1] border-[#B79256]/40"
          maxLength={60}
        />
        <Input
          data-testid="shop-form-price"
          value={form.price_hint}
          onChange={change("price_hint")}
          placeholder="Price hint (optional)"
          className="text-sm rounded-xl bg-[#FBF8F1] border-[#B79256]/40"
          maxLength={40}
        />
      </div>
      {/* Related-branch picker — only surfaces once the author's own
          branches are loaded AND there's at least one other branch they
          could point to. Keeps the form quiet when it's not usable. */}
      {Array.isArray(myBranches) && myBranches.length > 0 && (
        <div>
          <label className="accent-label text-[10px] text-[#B79256] block mb-1">
            Also link to a branch you wrote about this?
          </label>
          <select
            data-testid="shop-form-related-idea"
            value={form.related_idea_id || ""}
            onChange={change("related_idea_id")}
            className="w-full h-10 rounded-xl border border-[#B79256]/40 bg-[#FBF8F1] px-3 text-sm text-[#2A2823]"
          >
            <option value="">— none —</option>
            {myBranches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title.length > 60 ? `${b.title.slice(0, 60)}…` : b.title}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-[#2A2823]/50 italic mt-1">
            When set, shoppers see "Read My Review" alongside the product link.
          </p>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          data-testid="shop-form-cancel"
          onClick={onCancel}
          className="text-xs text-[#2A2823]/60 hover:text-[#2A2823] transition-colors px-2"
        >
          Cancel
        </button>
        <Button
          data-testid="shop-form-submit"
          onClick={onSubmit}
          disabled={busy}
          className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 shadow-none text-xs"
        >
          Save
        </Button>
      </div>
    </div>
  );
}
